import {getId, getType} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {Formik} from 'formik'
import {minutesFromDayjs, dayjsFromSeconds} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {compose, fromRenderProps} from 'recompose'
import ExamContext, {ExamProvider, formSchema,} from 'views/AppLocals/Exam/ExamContext'
import ExaminationContext from 'views/AppLocals/Exam/ExaminationContext'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {BsExclamationCircleFill, BsFillMenuButtonWideFill, BsShieldFillExclamation} from "react-icons/bs";

const ExaminationSettingOverview = () => {
  const t = useTranslate()

  const {onUpdate = NullAsDefault} = useContext(ExamContext)

  const {examination} = useContext(ExaminationContext)

  const _initialValues = useMemo(() => {
    const {cover_photo, timelimit = 0, categories = [], ...rest} = examination || {}
    const _timelimit = dayjsFromSeconds(timelimit * 60)
    return {
      ...rest,
      timelimit: _timelimit,
      cover_photo,
      cover_photo_preview: cover_photo,
      categories: _.isArray(categories)
        ? categories.map((category) => category?.id)
        : [],
    }
  }, [examination]);

  const validate = (values) => {
    let errors = {}

    const {title, timelimit} = values || {}

    if (!title) {
      errors.title = t('required field')
    }

    if (!timelimit) {
      errors.timelimit = t('required field')
    }

    return errors
  }

  const handleSubmit = ({avatar_preview, cover_photo_preview, ...values}) => {
    onUpdate(values, {id: getId(examination)})
  }

  return (
    <div className="space-y-3 flex flex-col">
      <Formik
        validate={validate}
        validateOnMount={true}
        validateOnChange={true}
        enableReinitialize={true}
        initialValues={_initialValues}
        onSubmit={(values) => {
          const changes = shallowDiff(values, _initialValues)
          const {timelimit, categories = []} = changes || {}
          handleSubmit(
            _.omitBy(
              {
                ...changes,
                timelimit: timelimit ? minutesFromDayjs(timelimit) : undefined,
                categories: categories
                  ? Array.from(categories).join(',')
                  : undefined,
              },
              _.isUndefined
            )
          )
        }}>
        <div className="flex flex-col gap-y-10">
          <FieldsFactory
            formSchema={[
              formSchema.cover_photo,
            ]}
          />
          <div className="flex flex-col gap-3">
            <HeaderWithIconLine
              icon={<BsExclamationCircleFill size={14} className="text-color-500"/>}
              title={t('basic information')}
              className="text-lg text-color-300"
            />
            <FieldsFactory
              formSchema={[
                formSchema.title,
                formSchema.description,
                formSchema.categories,
                formSchema.postscript,
                formSchema.edit_started_finished_date,
              ]}
            />
          </div>
          <div className="flex flex-col gap-3">
            <HeaderWithIconLine
              icon={<BsFillMenuButtonWideFill size={14} className="text-color-500"/>}
              title={t('exam regulations')}
              className="text-lg text-color-300"
            />
            <FieldsFactory
              formSchema={[
                formSchema.edit_boolean_1,
                formSchema.examination_paper_condition,
                formSchema.score_group,
              ]}
            />
          </div>
          <div className="flex flex-col gap-3">
            <HeaderWithIconLine
              icon={<BsShieldFillExclamation size={14} className="text-color-500"/>}
              title={t('privacy')}
              className="text-lg text-color-300"
            />
            <FieldsFactory formSchema={[
              formSchema.privacy__active,
              formSchema.display_result_on_final
            ]}/>
          </div>
          <FormActionBar className="background"/>
        </div>
      </Formik>
    </div>
  )
}

export default compose(
  fromRenderProps(ExaminationContext.Consumer, ({examination}) => ({
    owner_id: getId(examination),
    owner_type: getType(examination),
  }))
)(({owner_id, owner_type}) => (
  <ExamProvider {...{owner_id, owner_type}}>
    <ExaminationSettingOverview/>
  </ExamProvider>
))
